export interface StorageBudget {
  totalBytes: number;
  usedBytes: number;
  budgetPercent: number;
  budgetBytes: number;
  availableBytes: number;
  isLow: boolean;
}

const CONSERVATIVE_TOTAL = 100 * 1024 * 1024; // 100 MB fallback
const LOW_THRESHOLD = 0.15;

function isIOS(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function getBudgetPercent(): number {
  return isIOS() ? 0.5 : 0.7;
}

export async function getStorageBudget(): Promise<StorageBudget> {
  try {
    if (navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      const totalBytes = estimate.quota ?? CONSERVATIVE_TOTAL;
      const usedBytes = estimate.usage ?? 0;
      const budgetPercent = getBudgetPercent();
      const budgetBytes = Math.floor(totalBytes * budgetPercent);
      const availableBytes = Math.max(0, budgetBytes - usedBytes);
      const isLow = availableBytes < totalBytes * LOW_THRESHOLD;

      return {
        totalBytes,
        usedBytes,
        budgetPercent,
        budgetBytes,
        availableBytes,
        isLow,
      };
    }
  } catch {
    // Degrade silently
  }

  const budgetPercent = getBudgetPercent();
  const budgetBytes = Math.floor(CONSERVATIVE_TOTAL * budgetPercent);

  return {
    totalBytes: CONSERVATIVE_TOTAL,
    usedBytes: 0,
    budgetPercent,
    budgetBytes,
    availableBytes: budgetBytes,
    isLow: false,
  };
}

export async function hasSpaceFor(bytes: number): Promise<boolean> {
  try {
    const budget = await getStorageBudget();
    return budget.availableBytes >= bytes;
  } catch {
    return false;
  }
}
